import { DateSubcondition, DateSubconditionType } from "./date-subcondition";

export interface Parameters {
  now: Temporal.Instant;
  // For date evaluation
  targetDates: Temporal.Instant[];
}

/**
 * Evaluates whether any of the target dates meet the date subcondition requirements
 */
export function evaluate(
  condition: DateSubcondition | null | undefined,
  parameters: Parameters,
): boolean {
  if (!condition) {
    return true; // If no condition is specified, all dates match
  }

  switch (condition.type) {
    case DateSubconditionType.ALL_TIME:
      return true; // Always matches if "All time" is selected

    case DateSubconditionType.ON_OR_BEFORE:
      return evaluateOnOrBefore(condition, parameters);

    case DateSubconditionType.ON_OR_AFTER:
      return evaluateOnOrAfter(condition, parameters);

    case DateSubconditionType.BETWEEN:
      return evaluateBetween(condition, parameters);

    case DateSubconditionType.LAST_N_DAYS:
      return evaluateLastNDays(condition, parameters);

    default:
      return false;
  }
}

/**
 * Count how many dates fall within the specified date condition
 */
export function countMatchingDates(
  condition: DateSubcondition | null | undefined,
  parameters: Parameters,
): number {
  if (!condition) {
    return parameters.targetDates.length; // If no condition, all dates match
  }

  if (condition.type === DateSubconditionType.LAST_N_DAYS) {
    return countDatesInLastNDays(condition, parameters);
  }

  // For other date types, count how many dates match the condition
  return parameters.targetDates.filter((date) => {
    switch (condition.type) {
      case DateSubconditionType.ALL_TIME:
        return true;

      case DateSubconditionType.ON_OR_BEFORE:
        return condition.beforeDate
          ? Temporal.Instant.compare(
              date,
              Temporal.Instant.from(condition.beforeDate),
            ) <= 0
          : false;

      case DateSubconditionType.ON_OR_AFTER:
        return condition.afterDate
          ? Temporal.Instant.compare(
              Temporal.Instant.from(condition.afterDate),
              date,
            ) <= 0
          : false;

      case DateSubconditionType.BETWEEN:
        return condition.startDate && condition.endDate
          ? Temporal.Instant.compare(
              Temporal.Instant.from(condition.startDate),
              date,
            ) <= 0 &&
              Temporal.Instant.compare(
                date,
                Temporal.Instant.from(condition.endDate),
              ) <= 0
          : false;

      default:
        return false;
    }
  }).length;
}

// Private helper methods

function evaluateOnOrBefore(
  condition: DateSubcondition,
  parameters: Parameters,
): boolean {
  if (!condition.beforeDate) {
    return false;
  }

  const beforeInstant = Temporal.Instant.from(condition.beforeDate);

  // At least one date must be on or before the specified date
  return parameters.targetDates.some(
    (date) => Temporal.Instant.compare(date, beforeInstant) <= 0,
  );
}

function evaluateOnOrAfter(
  condition: DateSubcondition,
  parameters: Parameters,
): boolean {
  if (!condition.afterDate) {
    return false;
  }

  const afterInstant = Temporal.Instant.from(condition.afterDate);

  // At least one date must be on or after the specified date
  return parameters.targetDates.some(
    (date) => Temporal.Instant.compare(afterInstant, date) <= 0,
  );
}

function evaluateBetween(
  condition: DateSubcondition,
  parameters: Parameters,
): boolean {
  if (!condition.startDate || !condition.endDate) {
    return false;
  }

  const startInstant = Temporal.Instant.from(condition.startDate);
  const endInstant = Temporal.Instant.from(condition.endDate);

  // At least one date must be between the start and end dates (inclusive)
  return parameters.targetDates.some(
    (date) =>
      Temporal.Instant.compare(startInstant, date) <= 0 &&
      Temporal.Instant.compare(date, endInstant) <= 0,
  );
}

function evaluateLastNDays(
  condition: DateSubcondition,
  parameters: Parameters,
): boolean {
  if (!condition.days) {
    return false;
  }

  const cutoffDate = parameters.now.subtract({ hours: 24 * condition.days });

  // At least one date must be within the last N days
  return parameters.targetDates.some(
    (date) => Temporal.Instant.compare(date, cutoffDate) >= 0,
  );
}

function countDatesInLastNDays(
  condition: DateSubcondition,
  parameters: Parameters,
): number {
  if (!condition.days) {
    return 0;
  }

  const cutoffDate = parameters.now.subtract({ hours: 24 * condition.days });

  // Count dates that fall within the last N days
  return parameters.targetDates.filter(
    (date) => Temporal.Instant.compare(date, cutoffDate) >= 0,
  ).length;
}
